import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

const AddProfile = ({ toastRef }) => {
  const navigate = useNavigate();
  const [loggedInEmail] = useState(localStorage.getItem('email'));

  useEffect(() => {
    if (!loggedInEmail) {
      toastRef.current.show({ severity: 'info', summary: 'Info', detail: 'Please login first', life: 3000 });
      navigate('/login');
    }
  }, [loggedInEmail, navigate]);

  const [profile, setProfile] = useState({
    name: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    maritalStatus: '',
    height: '',
    weight: '',
    religion: '',
    caste: '',
    motherTongue: '',
    nationality: '',
    email: loggedInEmail || '',
    phone: '',
    education: '',
    occupation: '',
    annualIncome: '',
    fatherOccupation: '',
    motherOccupation: '',
    siblings: '',
    familyType: '',
    foodHabits: '',
    smokingHabit: '',
    drinkingHabit: '',
    languagesKnown: '',
    aboutYourself: '',
    partnerAgeRange: '',
    partnerHeightRange: '',
    partnerReligion: '',
    partnerCaste: '',
    partnerLocation: '',
    partnerEducation: '',
    partnerOccupation: '',
    profileImage: null
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const handleProfileChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      if (files.length > 0) {
        setImageLoading(true);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
          setImageLoading(false);
          setProfile(prev => ({
            ...prev,
            profileImage: files[0]
          }));
        };
        reader.readAsDataURL(files[0]);
      }
    } else {
      setProfile(prev => ({ ...prev, [name]: value }));
    }
  };

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      let base64Image = null;
      if (profile.profileImage) {
        base64Image = await toBase64(profile.profileImage);
      }

      const payload = {
        ...profile,
        profileImageData: base64Image,
        email: loggedInEmail || profile.email, // ensure email from login or form
        profileImage: undefined // remove profileImage file from payload
      };

      // Remove profileImage field to avoid sending file object
      delete payload.profileImage;

      await axios.post(`${apiBaseUrl}/api/matchmaking/addProfile`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      toastRef.current.show({ severity: 'success', summary: 'Success', detail: 'Profile added successfully!', life: 3000 });

      // Reset form and preview
      setProfile({
        name: '',
        dateOfBirth: '',
        age: '',
        gender: '',
        maritalStatus: '',
        height: '',
        weight: '',
        religion: '',
        caste: '',
        motherTongue: '',
        nationality: '',
        email: loggedInEmail || '',
        phone: '',
        education: '',
        occupation: '',
        annualIncome: '',
        fatherOccupation: '',
        motherOccupation: '',
        siblings: '',
        familyType: '',
        foodHabits: '',
        smokingHabit: '',
        drinkingHabit: '',
        languagesKnown: '',
        aboutYourself: '',
        partnerAgeRange: '',
        partnerHeightRange: '',
        partnerReligion: '',
        partnerCaste: '',
        partnerLocation: '',
        partnerEducation: '',
        partnerOccupation: '',
        profileImage: null
      });
      setImagePreview(null);

    } catch (error) {
      console.error('Submit error:', error);
      toastRef.current.show({ severity: 'error', summary: 'Error', detail: 'Profile submission failed.', life: 3000 });
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-gray-900 shadow-lg rounded-lg">
      <h2 className="text-2xl font-semibold text-center mb-6 text-blue-300">Add Profile</h2>
      <form onSubmit={handleProfileSubmit} className="p-fluid matchmaking-form">
        {/* Render input fields like in your original AddProfile component here,
            with onChange calling handleProfileChange and controlled values from profile state */}
        {/* Example: */}
        <div className="p-field">
          <label htmlFor="name" className="text-blue-300">Name</label>
          <input type="text" id="name" name="name" value={profile.name} onChange={handleProfileChange} required className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>

        <div className="p-field">
          <label htmlFor="dateOfBirth" className="text-blue-300">Date of Birth</label>
          <input type="date" id="dateOfBirth" name="dateOfBirth" value={profile.dateOfBirth} onChange={handleProfileChange} required className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="age" className="text-blue-300">Age</label>
          <input type="number" id="age" name="age" value={profile.age} onChange={handleProfileChange} required min="18" max="100" className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="gender" className="text-blue-300">Gender</label>
          <select id="gender" name="gender" value={profile.gender} onChange={handleProfileChange} required className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" >
            <option value="">Select gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="p-field">
          <label htmlFor="maritalStatus" className="text-blue-300">Marital Status</label>
          <select id="maritalStatus" name="maritalStatus" value={profile.maritalStatus} onChange={handleProfileChange} required className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" >
            <option value="">Select marital status</option>
            <option value="Never Married">Never Married</option>
            <option value="Married">Married</option>
            <option value="Divorced">Divorced</option>
            <option value="Widowed">Widowed</option>
          </select>
        </div>
        <div className="p-field">
          <label htmlFor="height" className="text-blue-300">Height (in cm)</label>
          <input type="number" id="height" name="height" value={profile.height} onChange={handleProfileChange} min="50" max="300" className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="weight" className="text-blue-300">Weight (in kgs)</label>
          <input type="number" id="weight" name="weight" value={profile.weight} onChange={handleProfileChange} min="20" max="200" className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="religion" className="text-blue-300">Religion</label>
          <select
            id="religion"
            name="religion"
            value={
              profile.religion === "" ? "Hindu" :
                ["Hindu", "Muslim", "Christian", "Sikh", "Jain", "Others"].includes(profile.religion)
                  ? profile.religion
                  : "Others"
            }
            onChange={handleProfileChange}
            className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm"
          >
            <option value="">Select religion</option>
            <option value="Hindu">Hindu</option>
            <option value="Muslim">Muslim</option>
            <option value="Christian">Christian</option>
            <option value="Sikh">Sikh</option>
            <option value="Others">Others</option>
          </select>
          {(profile.religion === "Others" || !["Hindu", "Muslim", "Christian", "Sikh", "Jain"].includes(profile.religion)) ? (
            <input
              type="text"
              id="religionOther"
              name="religion"
              value={["Hindu", "Muslim", "Christian", "Sikh", "Jain"].includes(profile.religion) ? "" : profile.religion}
              onChange={handleProfileChange}
              placeholder="Please specify religion"
              className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm mt-2"
            />
          ) : null}
        </div>
        <div className="p-field">
          <label htmlFor="caste" className="text-blue-300">Caste/Sub-caste</label>
          <input type="text" id="caste" name="caste" value={profile.caste} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="motherTongue" className="text-blue-300">Mother Tongue</label>
          <input type="text" id="motherTongue" name="motherTongue" value={profile.motherTongue} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="nationality" className="text-blue-300">Nationality</label>
          <input type="text" id="nationality" name="nationality" value={profile.nationality} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="email" className="text-blue-300">Email</label>
          <input type="email" id="email" name="email" value={profile.email} onChange={handleProfileChange} required className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="phone" className="text-blue-300">Phone Number</label>
          <input type="tel" id="phone" name="phone" value={profile.phone} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="education" className="text-blue-300">Educational Qualification</label>
          <input type="text" id="education" name="education" value={profile.education} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="occupation" className="text-blue-300">Occupation</label>
          <input type="text" id="occupation" name="occupation" value={profile.occupation} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="annualIncome" className="text-blue-300">Annual Income</label>
          <input type="number" id="annualIncome" name="annualIncome" value={profile.annualIncome} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="fatherOccupation" className="text-blue-300">Father's Occupation</label>
          <input type="text" id="fatherOccupation" name="fatherOccupation" value={profile.fatherOccupation} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="motherOccupation" className="text-blue-300">Mother's Occupation</label>
          <input type="text" id="motherOccupation" name="motherOccupation" value={profile.motherOccupation} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="siblings" className="text-blue-300">Number of Siblings</label>
          <input type="number" id="siblings" name="siblings" value={profile.siblings} onChange={handleProfileChange} min="0" max="20" className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="familyType" className="text-blue-300">Family Type</label>
          <select id="familyType" name="familyType" value={profile.familyType} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" >
            <option value="">Select family type</option>
            <option value="Nuclear">Nuclear</option>
            <option value="Joint">Joint</option>
          </select>
        </div>
        <div className="p-field">
          <label htmlFor="foodHabits" className="text-blue-300">Food Habits</label>
          <select id="foodHabits" name="foodHabits" value={profile.foodHabits} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" >
            <option value="">Select food habit</option>
            <option value="Vegetarian">Vegetarian</option>
            <option value="Non-Vegetarian">Non-Vegetarian</option>
            <option value="Eggetarian">Eggetarian</option>
          </select>
        </div>
        <div className="p-field">
          <label htmlFor="smokingHabit" className="text-blue-300">Smoking Habit</label>
          <select id="smokingHabit" name="smokingHabit" value={profile.smokingHabit} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" >
            <option value="">Select smoking habit</option>
            <option value="No">No</option>
            <option value="Occasionally">Occasionally</option>
            <option value="Regularly">Regularly</option>
          </select>
        </div>
        <div className="p-field">
          <label htmlFor="drinkingHabit" className="text-blue-300">Drinking Habit</label>
          <select id="drinkingHabit" name="drinkingHabit" value={profile.drinkingHabit} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" >
            <option value="">Select drinking habit</option>
            <option value="No">No</option>
            <option value="Occasionally">Occasionally</option>
            <option value="Regularly">Regularly</option>
          </select>
        </div>
        <div className="p-field">
          <label htmlFor="languagesKnown" className="text-blue-300">Languages Known</label>
          <input type="text" id="languagesKnown" name="languagesKnown" value={profile.languagesKnown} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="aboutYourself" className="text-blue-300">About Yourself</label>
          <textarea id="aboutYourself" name="aboutYourself" value={profile.aboutYourself} onChange={handleProfileChange} rows={4} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>

        <h3 className="text-blue-300">Partner Preferences</h3>

        <div className="p-field">
          <label htmlFor="partnerAgeRange" className="text-blue-300">Age Range</label>
          <input type="text" id="partnerAgeRange" name="partnerAgeRange" value={profile.partnerAgeRange} onChange={handleProfileChange} placeholder="e.g. 25-30" className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="partnerHeightRange" className="text-blue-300">Height Range (cm)</label>
          <input type="text" id="partnerHeightRange" name="partnerHeightRange" value={profile.partnerHeightRange} onChange={handleProfileChange} placeholder="e.g. 160-175" className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="partnerReligion" className="text-blue-300">Religion</label>
          <div className="p-field">
            <select
              id="partnerReligion"
              name="partnerReligion"
              value={profile.partnerReligion}
              onChange={handleProfileChange}
              required
              className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm"
            >
              <option value="">Select religion</option>
              <option value="Hindu">Hindu</option>
              <option value="Muslim">Muslim</option>
              <option value="Sikh">Sikh</option>
              <option value="Christian">Christian</option>
              <option value="Any">Any</option>
            </select>
          </div>
        </div>
        <div className="p-field">
          <label htmlFor="partnerCaste" className="text-blue-300">Caste/Sub-caste</label>
          <input type="text" id="partnerCaste" name="partnerC'aste" value={profile.partnerCaste} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="partnerLocation" className="text-blue-300">Location</label>
          <input type="text" id="partnerLocation" name="partnerLocation" value={profile.partnerLocation} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="partnerEducation" className="text-blue-300">Education</label>
          <input type="text" id="partnerEducation" name="partnerEducation" value={profile.partnerEducation} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>
        <div className="p-field">
          <label htmlFor="partnerOccupation" className="text-blue-300">Occupation</label>
          <input type="text" id="partnerOccupation" name="partnerOccupation" value={profile.partnerOccupation} onChange={handleProfileChange} className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm" />
        </div>

        <div className="p-field">
          <label htmlFor="profileImage" className="text-blue-300">Profile Image</label>
          <input
            type="file"
            id="profileImage"
            name="profileImage"
            accept="image/*"
            onChange={handleProfileChange}
            className="w-full px-3 py-2 bg-gray-800 text-blue-300 border border-blue-500 rounded-md shadow-sm"
          />
          {imageLoading && <div className="text-blue-300">Loading image...</div>}
          {imagePreview && !imageLoading && (
            <img src={imagePreview} alt="Profile Preview" className="mt-2 max-h-48 rounded-md" />
          )}
        </div>

        <Button type="submit" label="Add Profile" className="p-mt-2 matchmaking-btn bg-blue-600 text-white" />
      </form>
    </div>
  );
};

export default AddProfile;
